import type { KeyCombination } from '@/lib/stores/keyboard-shortcuts/keyboard-shortcuts-store';

import { useKeyboardShortcutsStore } from '@/lib/stores/keyboard-shortcuts/keyboard-shortcuts-store';

/**
 * Wraps a handler function to suppress a standalone key press when a key combination is used.
 *
 * This utility is designed to prevent conflicts where a single key (e.g., 'c')
 * and a key combination (e.g., 'c+b') might trigger unintended actions simultaneously.
 * It marks the specified key as "used in a combination" for a short duration,
 * allowing the combination's action to proceed without triggering the single key's action.
 */
export function suppressKeyWithFunc<Args extends unknown[]>(
  key: KeyCombination,
  handler: (...args: Args) => void,
  delay = 1000,
): (...args: Args) => void {
  const { setKeyCombinationUsed } = useKeyboardShortcutsStore.getState();

  return (...args: Args) => {
    // Mark that a key combination was used
    setKeyCombinationUsed(key, true);

    // Reset flag after delay
    setTimeout(() => setKeyCombinationUsed(key, false), delay);

    // Invoke original handler
    handler(...args);
  };
}
